import { Component, OnDestroy, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

interface PartnerCarrierApiResponse<T = unknown> {
  success?: boolean;
  code?: string | number;
  status?: number;
  message?: string;
  data?: T;
  response?: T;
  contact_relationship_uuid?: string;
}

interface PartnerCarrierRelationship {
  contact_relationship_uuid?: string | null;
  target_contact_uuid?: string | null;
  is_global?: boolean | string | null;
  partner_contact_organization?: string | null;
  partner_legal_name?: string | null;
  partner_contact_email?: string | null;
  partner_contact_scac?: string | null;
  partner_carrier_mc?: string | null;
  partner_us_dot_no?: string | null;
  partner_active?: boolean | string | null;
}

interface PartnerCarrierContact {
  contact_uuid?: string | null;
  contact_organization?: string | null;
  legal_name?: string | null;
  contact_email?: string | null;
  contact_scac?: string | null;
  carrier_mc?: string | null;
  us_dot_no?: string | null;
  active?: boolean | string | null;
  contact_relationship_uuid?: string | null;
  is_global?: boolean | string | null;
}

export interface DropTrailerPartnerCarrierEditValue {
  name: string | null;
  contact_uuid: string | null;
  relationship_uuid?: string | null;
  is_override?: boolean;
  previous_contact_uuid?: string | null;
}

@Component({
  selector: 'app-drop-trailer-partner-carrier-cell-editor',
  templateUrl: './drop-trailer-partner-carrier-cell-editor.component.html',
  styleUrls: ['./drop-trailer-partner-carrier-cell-editor.component.scss']
})
export class DropTrailerPartnerCarrierCellEditorComponent implements ICellEditorAngularComp, OnDestroy {
  @ViewChild('contactSearch') contactSearch?: { focus?: () => void; open?: () => void };

  private readonly apiUrl = '/api/partner_carriers/partner_carriers.php';
  private params!: ICellEditorParams;
  private sourceCarrierUuid = '';
  private sourceCarrierName = '';
  private currentPartnerName = '';
  private previousContactUuid: string | null = null;
  private selectedValue: DropTrailerPartnerCarrierEditValue | null = null;
  private searchTimer: ReturnType<typeof window.setTimeout> | null = null;

  partnerControl = new FormControl<PartnerCarrierContact | null>(null);
  searchControl = new FormControl<PartnerCarrierContact | null>(null);
  partners: PartnerCarrierContact[] = [];
  contactOptions: PartnerCarrierContact[] = [];
  loadingPartners = false;
  searchingContacts = false;
  savingPartner = false;
  showSearch = false;
  errorMessage = '';
  carrierLimitedMode = false;

  constructor(private http: HttpClient) {}

  agInit(params: ICellEditorParams): void {
    this.params = params;
    const row = params.data || {};
    this.carrierLimitedMode = !!(params as any).carrierLimitedMode;
    this.sourceCarrierUuid = this.firstString(row.carrier_contact_uuid, row.carrier_uuid, row.carrier_contact_id);
    this.sourceCarrierName = this.firstString(row.carrier_name, row.carrier_contact_organization);
    this.currentPartnerName = this.firstString(row.interline_carrier_name, params.value);
    this.previousContactUuid = this.nullIfBlank(row.interline_carrier_contact_uuid);
    this.loadPartners();
  }

  ngOnDestroy(): void {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }
  }

  getValue(): DropTrailerPartnerCarrierEditValue {
    if (this.selectedValue) {
      return this.selectedValue;
    }

    const partner = this.partnerControl.value;
    if (partner) {
      return this.valueFromContact(partner);
    }

    return this.buildValue(this.currentPartnerName || null, this.previousContactUuid);
  }

  isPopup(): boolean {
    return true;
  }

  get canOverride(): boolean {
    return !this.carrierLimitedMode;
  }

  get hasSourceCarrier(): boolean {
    return !!this.sourceCarrierUuid;
  }

  get sourceLabel(): string {
    return this.sourceCarrierName || 'Carrier';
  }

  optionLabel(option: PartnerCarrierContact | null): string {
    if (!option) return '';
    return this.firstString(
      option.contact_organization,
      option.legal_name,
      option.contact_scac,
      option.carrier_mc,
      option.us_dot_no,
      option.contact_uuid
    ) || 'Unnamed carrier';
  }

  optionMeta(option: PartnerCarrierContact): string {
    return [
      option.contact_scac ? `SCAC ${option.contact_scac}` : '',
      option.carrier_mc ? `MC ${option.carrier_mc}` : '',
      option.us_dot_no ? `DOT ${option.us_dot_no}` : '',
      this.isInactive(option) ? 'Inactive' : '',
      this.isGlobal(option) ? 'Global' : ''
    ].filter(Boolean).join(' | ');
  }

  isExistingPartner(option: PartnerCarrierContact): boolean {
    const contactUuid = this.nullIfBlank(option.contact_uuid);
    if (!contactUuid) return false;
    return this.partners.some((partner) => this.nullIfBlank(partner.contact_uuid) === contactUuid);
  }

  onPartnerSelected(option: PartnerCarrierContact | null): void {
    if (!option) {
      this.clearValue();
      return;
    }

    this.choosePartner(option);
  }

  openSearch(): void {
    this.showSearch = true;
    this.errorMessage = '';
    this.searchControl.setValue(null, { emitEvent: false });
    this.contactOptions = [];

    window.setTimeout(() => {
      this.contactSearch?.focus?.();
      this.contactSearch?.open?.();
    });
  }

  onContactSearch(event: { term?: string } | string): void {
    const term = typeof event === 'string' ? event : event?.term || '';
    const search = term.trim();

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    if (search.length < 2) {
      this.contactOptions = [];
      return;
    }

    this.searchTimer = window.setTimeout(() => this.searchContacts(search), 250);
  }

  serverSearchFilter(_: string, __: PartnerCarrierContact): boolean {
    return true;
  }

  onContactSelected(option: PartnerCarrierContact | null): void {
    if (!option) return;

    const existingPartner = this.partnerByUuid(this.nullIfBlank(option.contact_uuid));
    if (existingPartner) {
      this.choosePartner(existingPartner);
      return;
    }

    if (this.sourceCarrierUuid) {
      this.addPartnerAndChoose(option);
      return;
    }

    if (this.canOverride) {
      this.selectedValue = this.valueFromContact(option, true);
      this.params.stopEditing();
      return;
    }

    this.errorMessage = 'Carrier account could not be resolved.';
  }

  clearValue(): void {
    this.selectedValue = this.buildValue(null, null);
    this.partnerControl.setValue(null, { emitEvent: false });
    this.params.stopEditing();
  }

  cancel(): void {
    this.params.api.stopEditing(true);
  }

  onEditorKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    }
  }

  trackOption(_: number, option: PartnerCarrierContact): string {
    return this.nullIfBlank(option.contact_relationship_uuid)
      || this.nullIfBlank(option.contact_uuid)
      || this.optionLabel(option);
  }

  private choosePartner(option: PartnerCarrierContact): void {
    this.selectedValue = this.valueFromContact(option);
    this.partnerControl.setValue(option, { emitEvent: false });
    this.params.stopEditing();
  }

  private valueFromContact(option: PartnerCarrierContact, isOverride = false): DropTrailerPartnerCarrierEditValue {
    return this.buildValue(
      this.optionLabel(option),
      this.nullIfBlank(option.contact_uuid),
      {
        relationship_uuid: this.nullIfBlank(option.contact_relationship_uuid),
        is_override: isOverride,
      }
    );
  }

  private loadPartners(): void {
    this.loadingPartners = true;
    const params: Record<string, string | number | boolean> = {
      include_global: true,
      limit: 200,
    };

    if (this.sourceCarrierUuid) {
      params['source_contact_uuid'] = this.sourceCarrierUuid;
    }

    this.apiGet<PartnerCarrierRelationship[]>('partner_carriers', params)
      .pipe(finalize(() => this.loadingPartners = false))
      .subscribe({
        next: (response) => {
          if (!this.isSuccess(response)) {
            this.errorMessage = response?.message || 'Partner carriers could not be loaded.';
            this.partners = this.currentPartnerOption();
            this.syncCurrentPartnerSelection();
            return;
          }

          this.errorMessage = '';
          this.partners = this.uniqueByContact([
            ...this.currentPartnerOption(),
            ...this.normalizeList<PartnerCarrierRelationship>(response).map((relationship) => this.relationshipToContact(relationship)),
          ]).sort((a, b) => this.optionLabel(a).localeCompare(this.optionLabel(b)));
          this.syncCurrentPartnerSelection();
        },
        error: () => {
          this.errorMessage = 'Partner carriers could not be loaded.';
          this.partners = this.currentPartnerOption();
          this.syncCurrentPartnerSelection();
        }
      });
  }

  private searchContacts(search: string): void {
    this.searchingContacts = true;
    this.errorMessage = '';

    const params: Record<string, string | number | boolean> = {
      search,
      limit: 40,
      include_existing: this.canOverride,
    };

    if (this.sourceCarrierUuid) {
      params['source_contact_uuid'] = this.sourceCarrierUuid;
    }

    this.apiGet<PartnerCarrierContact[]>('carrier_search', params).pipe(
      catchError(() => of(null)),
      finalize(() => this.searchingContacts = false),
    ).subscribe((response) => {
      if (!response || !this.isSuccess(response)) {
        this.contactOptions = [];
        this.errorMessage = response?.message || 'Carrier search could not be loaded.';
        return;
      }

      this.contactOptions = this.uniqueByContact(this.normalizeList<PartnerCarrierContact>(response))
        .filter((contact) => this.nullIfBlank(contact.contact_uuid))
        .sort((a, b) => this.optionLabel(a).localeCompare(this.optionLabel(b)));
    });
  }

  private addPartnerAndChoose(option: PartnerCarrierContact): void {
    const contactUuid = this.nullIfBlank(option.contact_uuid);
    if (!contactUuid) return;

    this.savingPartner = true;
    const body: Record<string, unknown> = {
      action: 'add_partner_carrier',
      target_contact_uuid: contactUuid,
    };

    if (this.sourceCarrierUuid) {
      body['source_contact_uuid'] = this.sourceCarrierUuid;
    }

    this.http.post<PartnerCarrierApiResponse<PartnerCarrierRelationship>>(this.apiUrl, body)
      .pipe(finalize(() => this.savingPartner = false))
      .subscribe({
        next: (response) => {
          if (!this.isSuccess(response)) {
            this.errorMessage = response?.message || 'Partner carrier could not be added.';
            return;
          }

          const relationship = response.data || null;
          const partner = relationship ? this.relationshipToContact(relationship) : option;
          this.partners = this.uniqueByContact([...this.partners, partner]);
          this.choosePartner({
            ...option,
            ...partner,
            contact_relationship_uuid: partner.contact_relationship_uuid || response.contact_relationship_uuid || null,
          });
        },
        error: (error) => {
          this.errorMessage = error?.error?.message || 'Partner carrier could not be added.';
        }
      });
  }

  private apiGet<T>(requestType: string, params: Record<string, string | number | boolean>): Observable<PartnerCarrierApiResponse<T>> {
    let httpParams = new HttpParams().set('request_type', requestType);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return this.http.get<PartnerCarrierApiResponse<T>>(this.apiUrl, { params: httpParams });
  }

  private relationshipToContact(relationship: PartnerCarrierRelationship): PartnerCarrierContact {
    return {
      contact_uuid: relationship.target_contact_uuid || null,
      contact_organization: relationship.partner_contact_organization || null,
      legal_name: relationship.partner_legal_name || null,
      contact_email: relationship.partner_contact_email || null,
      contact_scac: relationship.partner_contact_scac || null,
      carrier_mc: relationship.partner_carrier_mc || null,
      us_dot_no: relationship.partner_us_dot_no || null,
      active: relationship.partner_active || null,
      contact_relationship_uuid: relationship.contact_relationship_uuid || null,
      is_global: relationship.is_global || false,
    };
  }

  private currentPartnerOption(): PartnerCarrierContact[] {
    if (!this.previousContactUuid && !this.currentPartnerName) return [];

    return [{
      contact_uuid: this.previousContactUuid,
      contact_organization: this.currentPartnerName || this.previousContactUuid,
    }];
  }

  private syncCurrentPartnerSelection(): void {
    const current = this.partnerByUuid(this.previousContactUuid);
    if (current) {
      this.partnerControl.setValue(current, { emitEvent: false });
    }
  }

  private partnerByUuid(contactUuid: string | null): PartnerCarrierContact | null {
    if (!contactUuid) return null;
    return this.partners.find((partner) => this.nullIfBlank(partner.contact_uuid) === contactUuid) || null;
  }

  private normalizeList<T>(response: PartnerCarrierApiResponse<T[]> | T[] | undefined | null): T[] {
    if (!response) return [];
    if (Array.isArray(response)) return response.filter(Boolean);
    if (Array.isArray(response.data)) return response.data.filter(Boolean);
    if (Array.isArray(response.response)) return response.response.filter(Boolean);
    return [];
  }

  private uniqueByContact(options: PartnerCarrierContact[]): PartnerCarrierContact[] {
    const seen = new Set<string>();
    return options.filter((option) => {
      const key = this.nullIfBlank(option.contact_uuid) || this.optionLabel(option).toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private isSuccess(response: PartnerCarrierApiResponse<unknown> | undefined | null): boolean {
    if (!response || response.success === false) return false;
    const code = Number(response.code ?? response.status ?? 200);
    return response.success === true || response.code === 200 || response.code === '200' || response.status === 200 || code < 400;
  }

  private buildValue(
    name: string | null,
    contactUuid: string | null,
    extra: Partial<DropTrailerPartnerCarrierEditValue> = {},
  ): DropTrailerPartnerCarrierEditValue {
    return {
      name: this.nullIfBlank(name),
      contact_uuid: this.nullIfBlank(contactUuid),
      previous_contact_uuid: this.previousContactUuid,
      ...extra,
    };
  }

  private isInactive(option: PartnerCarrierContact): boolean {
    return option.active === false || String(option.active || '').toLowerCase() === 'f';
  }

  private isGlobal(option: PartnerCarrierContact): boolean {
    return option.is_global === true || String(option.is_global || '').toLowerCase() === 't';
  }

  private firstString(...values: unknown[]): string {
    for (const value of values) {
      const normalized = this.nullIfBlank(value);
      if (normalized) return normalized;
    }

    return '';
  }

  private nullIfBlank(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
  }
}
